import React from 'react'
import { Field, Form } from 'react-final-form'
import FormTextField from '../../../components/fields/FormTextField'
import { FormattedMessage, useIntl } from 'react-intl'
import Loader from '../../../components/loader/Loader'
import Button from '../../../components/button/Button'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../../styles'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { UpdateProfileModel } from '../profileModel'
import Link from '../../../components/link/Link'
import { useMediaQuery } from 'react-responsive'
import { UPDATE_INFO } from '../../../enums/profileEnum'
import FormPhoneField from '../../../components/fields/FormPhoneField'
import FormAutocompleteField from '../../../components/fields/FormAutocompleteField'
import suggestEmail from '../../../api/suggestEmail'

const useStyle = makeStyles(
    (theme) => (
        {
            ...styles(theme),
            formStyle: {
                marginTop: 40,
                marginBottom: 20
            },
            fieldStyle: {
                marginBottom: 25
            },
            footerContainerStyle: {
                marginTop: 30
            },
            buttonContainerStyle: {
                alignItems: 'center'
            },
            spinnerStyle: {
                textAlign: 'center'
            },
            errorStyle: {
                color: theme.palette.error.main,
                justifyContent: 'center'
            },
            buttonSmallStyle: {
                flexDirection: 'column-reverse',
                alignContent: 'center'
            },
            link: {
                marginTop: 10
            }
        }
    )
)

interface AcceptingProps {
    updateInformation: string,
    onCloseDialog: () => void,
    initialValues: UpdateProfileModel,
    onSubmit: (values: UpdateProfileModel) => void,
    validate: (values: UpdateProfileModel) => any,
}

type PersonalProps = AcceptingProps

const PersonalForm: React.FC<PersonalProps> = (
    {
        updateInformation,
        onCloseDialog,
        initialValues,
        onSubmit,
        validate
    }
) => {
    const classes = useStyle()
    const intl = useIntl()

    const isLargeScreen = useMediaQuery(
        {
            query: '(min-width: 1025px)'
        }
    )

    const formClass: ClassValue = classNames(
        'row',
        classes.formStyle
    )

    const fieldClass: ClassValue = classNames(
        'col-xs-12',
        classes.fieldStyle
    )

    const footerContainerClass: ClassValue = classNames(
        'col-xs-12 row end-xs',
        classes.footerContainerStyle
    )

    const buttonContainerClass: ClassValue = classNames(
        'col-xs-12 col-md-6 row',
        classes.buttonContainerStyle,
        {
            [classes.buttonSmallStyle]: !isLargeScreen
        }
    )

    const errorClass: ClassValue = classNames(
        'col-xs-12',
        classes.text1,
        classes.errorStyle
    )

    const linkClass: ClassValue = classNames(
        'col-xs-12 col-md-5',
        {
            [classes.link]: !isLargeScreen
        }
    )

    return <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={
            (
                {
                    handleSubmit,
                    submitting,
                    pristine,
                    submitError
                }
            ) => (
                <form
                    onSubmit={handleSubmit}
                    className={formClass}
                >
                    {UPDATE_INFO.EMAIL === updateInformation && <>
						<div className={fieldClass}>
							<Field
								name="email"
								label={intl.formatMessage({
                                    id: 'profile.newEmail',
                                    defaultMessage: 'NOUVEAU COURRIEL',
                                    description: 'New email input'
                                })}
                                component={FormAutocompleteField}
                                fetchOptions={(param: string) => suggestEmail(param)}
							/>
						</div>
						<div className={fieldClass}>
							<Field
								name="confirmEmail"
								label={intl.formatMessage({
                                    id: 'profile.emailConfirm',
                                    defaultMessage: 'CONFIRMATION DU COURRIEL',
                                    description: 'Email confirmation input'
                                })}
								component={FormTextField}
                                disablePaste
							/>
						</div>
					</>}
                    {UPDATE_INFO.PASSWORD === updateInformation && <>
						<div className={fieldClass}>
							<Field
								name="previousPassword"
								type="password"
								label={intl.formatMessage({
                                    id: 'profile.previousPassword',
                                    defaultMessage: 'ANCIEN MOT DE PASSE',
                                    description: 'Old password input'
                                })}
								component={FormTextField}
							/>
						</div>
						<div className={fieldClass}>
							<Field
								name="password"
								type="password"
								label={intl.formatMessage({
                                    id: 'profile.newPassword',
                                    defaultMessage: 'NOUVEAU MOT DE PASSE',
                                    description: 'New Password input'
                                })}
								component={FormTextField}
							/>
						</div>
					</>}
                    {UPDATE_INFO.PHONE === updateInformation &&
                        <div className={fieldClass}>
                            <Field
                                name="phone"
                                label={intl.formatMessage({
                                    id: 'profile.phone',
                                    defaultMessage: 'TÉLÉPHONE MOBILE',
                                    description: 'Phone information'
                                })}
                                component={FormPhoneField}
                            />
                        </div>
                    }

                    {submitError && <div className={errorClass}>{submitError}</div>}

                    <div className={footerContainerClass}>
                        <div className={buttonContainerClass}>
                            {
                                submitting ? (
                                    <div className={classes.spinnerStyle}>
                                        <Loader />
                                    </div>
                                ) : <>
                                    <div className={linkClass}>
                                        <Link
                                            color="colorAccessibility"
                                            textStyle="text4"
                                            onClick={onCloseDialog}
                                            hasChevron={false}
                                            hasChevronLeft
                                        >
                                            <FormattedMessage
                                                id="global.btn.cancel"
                                                defaultMessage="Annuler"
                                                description="Cancel update profile"
                                            />
                                        </Link>
                                    </div>
                                    <div className="col-xs-12 col-md-7">
                                        <Button
                                            background="backgroundAccessibility"
                                            color="colorWhite"
                                            textStyle="text8"
                                            type="submit"
                                            disabled={pristine}
                                        >
                                            <FormattedMessage
                                                id="global.btn.save"
                                                defaultMessage="Enregistrer"
                                                description="Submit update profile"
                                            />
                                        </Button>
                                    </div>
                                </>

                            }
                        </div>
                    </div>
                </form>
            )
        }
    />
}

export default PersonalForm
